import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { useToast } from '../../hooks/useToast';
import {
  IndividualMemberDto,
  MembershipType,
  getMemberDisplayFirstName,
  getMemberDisplayLastName,
} from '../../types/member';
import { CommunicationLanguage } from '../../types/auth';
import { adminService } from '../../services/admin';
import { committeeService } from '../../services/committees';
import { InternationalCommittee } from '../../types/committee';
import FormField from '../../components/common/FormField';
import Modal from '../../components/common/Modal';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import MemberProfileSectionEditor, {
  type ProfileSectionId,
} from '../../components/profile/MemberProfileSectionEditor';
import { VignetteStatusBadge } from '../../components/admin/VignetteStatusBadge';
import { Button } from '../../design-system/components/Button';
import { Card } from '../../design-system/components/Card';
import { Heading, Text } from '../../design-system/components/Typography';
import { Stack, Container } from '../../design-system/components/Layout';
import { spacing } from '../../design-system/tokens/spacing';
import { typography } from '../../design-system/tokens/typography';
import { resolveVignetteStatus } from '../../utils/vignette';
import { getApiErrorMessage, getValidationErrorMessage } from '../../utils/apiErrors';
import {
  COMMITTEES_INFO_LIST,
  MemberFormData,
  buildMemberUpdateDto,
  memberDtoToFormData,
} from '../../utils/memberForm';
import { validateStudiesList } from '../../utils/studyValidation';
import * as validation from '../../utils/validation';

const FormContainer = styled(Container)`
  max-width: 1000px;
  padding: ${spacing[10]} ${spacing[5]};
  background: ${props => props.theme.colors.gradientBackground};
  @media (max-width: 640px) {
    padding: ${spacing[8]} ${spacing[3]};
  }
`;

const CardWithPadding = styled(Card)`
  padding: ${spacing[12]};
  @media (max-width: 640px) {
    padding: ${spacing[6]};
  }
`;

const HeadingWithMargin = styled(Heading)`
  margin-bottom: ${spacing[2]};
`;

const CenteredDiv = styled.div`
  text-align: center;
`;

const BackLink = styled(Link)`
  color: ${props => props.theme.colors.primary};
  text-decoration: none;
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  font-family: ${typography.fontFamily.primary};
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
  margin-bottom: 24px;
  transition: color 0.2s ease;
  &:hover {
    color: ${props => props.theme.colors.primaryDark};
    text-decoration: underline;
  }
`;

const VignetteNote = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing[3]};
`;

const TextWithMargin = styled(Text)`
  margin-bottom: ${spacing[4]};
  display: block;
`;

const ExternalLink = styled.a`
  color: ${props => props.theme.colors.primary};
  margin-bottom: ${spacing[4]};
  display: block;
`;

const CommitteesList = styled.ul`
  margin: ${spacing[4]} 0 0 0;
  padding-left: ${spacing[6]};
  color: ${props => props.theme.colors.text};
  font-size: ${typography.fontSize.sm};
  line-height: 1.6;
`;

type AdminMemberForm = MemberFormData & { registrationYear: number };

const PERSONAL_SNAPSHOT_KEYS = ['email', 'registrationYear', 'isHonoraryMember'];

function communicationLanguage(language: string): CommunicationLanguage {
  const value = language.toLowerCase();
  if (value.startsWith('el') || value.startsWith('gr')) return CommunicationLanguage.Greek;
  if (value.startsWith('fr')) return CommunicationLanguage.French;
  if (value.startsWith('es')) return CommunicationLanguage.Spanish;
  return CommunicationLanguage.English;
}

function toAdminForm(
  member: IndividualMemberDto,
  committees: InternationalCommittee[],
  language: string
): AdminMemberForm {
  return {
    ...memberDtoToFormData(member, committees, communicationLanguage(language)),
    registrationYear: member.registrationYear ?? new Date().getFullYear(),
  };
}

function nameAnchor(form: AdminMemberForm): Partial<IndividualMemberDto> {
  const name = {
    firstNameEn: form.firstNameEn.trim() || undefined,
    firstNameGr: form.firstNameGr.trim() || undefined,
    lastNameEn: form.lastNameEn.trim() || undefined,
    lastNameGr: form.lastNameGr.trim() || undefined,
  };
  if (!name.firstNameEn && !name.firstNameGr && !name.lastNameEn && !name.lastNameGr) {
    return {};
  }
  return { name };
}

function buildAdminSectionPayload(
  form: AdminMemberForm,
  member: IndividualMemberDto,
  committees: InternationalCommittee[],
  sectionId: ProfileSectionId
): Partial<IndividualMemberDto> {
  const base = buildMemberUpdateDto(form, member, committees, sectionId);
  if (sectionId === 'personal') {
    return {
      ...base,
      email: form.email.trim(),
      membershipType: form.membershipType,
      registrationYear: form.registrationYear,
      isHonoraryMember: form.membershipType === MembershipType.Honorary,
    };
  }
  if (sectionId === 'work') {
    return {
      ...base,
      ...nameAnchor(form),
      isVolunteer: form.isVolunteer,
    };
  }
  return { ...base, ...nameAnchor(form) };
}

const AdminEditMember: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { success: showSuccess, error: showError } = useToast();
  const [member, setMember] = useState<IndividualMemberDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [committees, setCommittees] = useState<InternationalCommittee[]>([]);
  const [committeesLoadError, setCommitteesLoadError] = useState<string | null>(null);
  const [committeesModalOpen, setCommitteesModalOpen] = useState(false);
  const [formData, setFormData] = useState<AdminMemberForm | null>(null);

  const loadCommittees = useCallback(async () => {
    setCommitteesLoadError(null);
    try {
      const data = await committeeService.getAllCommittees();
      const active = data.filter(c => c.isActive);
      setCommittees(active);
      return active;
    } catch (err) {
      const message = err instanceof Error ? err.message : t('register.committees.loadError');
      setCommitteesLoadError(message);
      return [] as InternationalCommittee[];
    }
  }, [t]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [data, activeCommittees] = await Promise.all([
          adminService.getMemberById(id),
          loadCommittees(),
        ]);
        if (cancelled) return;
        setMember(data);
        setFormData(toAdminForm(data, activeCommittees, i18n.language));
      } catch (err) {
        if (cancelled) return;
        showError(err instanceof Error ? err.message : t('admin.member.loadError'));
        setError(t('profile.notFound'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // Reload only when the member id changes. Language is read at load time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const validateSection = (sectionId: ProfileSectionId): boolean => {
    if (!formData) return false;
    setError(null);
    const errors: Record<string, string> = {};
    let isValid = true;
    const { working: isWorking, studying: isStudying } = formData.professionalActivities;

    if (sectionId === 'personal') {
      if (!formData.email.trim()) {
        errors.email = t('validation.emailRequired');
        isValid = false;
      } else {
        const emailResult = validation.validateEmail(formData.email.trim());
        if (!emailResult.isValid) {
          errors.email = getValidationErrorMessage(
            emailResult.error || 'Please enter a valid email address',
            t
          );
          isValid = false;
        }
      }
      const hasFirst = !!(formData.firstNameEn?.trim() || formData.firstNameGr?.trim());
      if (!hasFirst) {
        errors.firstName = t('register.firstNameRequired');
        isValid = false;
      }
      const lastNameErrorKey = validation.bilingualLastNameErrorKey(
        formData.lastNameEn,
        formData.lastNameGr
      );
      if (lastNameErrorKey) {
        errors.lastName = t(lastNameErrorKey);
        isValid = false;
      }
      const dobPartsResult = validation.validateOptionalDateParts(
        {
          day: formData.dateOfBirthDay,
          month: formData.dateOfBirthMonth,
          year: formData.dateOfBirthYear,
        },
        formData.dateOfBirth
      );
      if (!dobPartsResult.isValid) {
        errors.dateOfBirth = getValidationErrorMessage(
          dobPartsResult.error || 'Please enter a valid date',
          t
        );
        isValid = false;
      }
      const phoneResult = validation.validatePhone(formData.phone ?? '', false);
      if (!phoneResult.isValid) {
        errors.phone = getValidationErrorMessage(
          phoneResult.error || 'validation.phoneInvalid',
          t
        );
        isValid = false;
      }
      const mobileResult = validation.validatePhone(formData.mobile ?? '', false);
      if (!mobileResult.isValid) {
        errors.mobile = getValidationErrorMessage(
          mobileResult.error || 'validation.phoneInvalid',
          t
        );
        isValid = false;
      }
    }

    if (sectionId === 'work') {
      if (isWorking) {
        const hasInstitution = !!(
          formData.professionalInstitutionNameEn?.trim() ||
          formData.professionalInstitutionNameGr?.trim()
        );
        if (!hasInstitution) {
          errors.professionalInstitution = t('register.professionalInstitutionRequired');
          isValid = false;
        }
      }
      const profEmail = formData.professionalEmail?.trim() ?? '';
      if (profEmail) {
        const emailResult = validation.validateEmail(profEmail);
        if (!emailResult.isValid) {
          errors.professionalEmail = getValidationErrorMessage(
            emailResult.error || 'Please enter a valid email address',
            t
          );
          isValid = false;
        }
      }
      const profPhoneResult = validation.validatePhone(formData.professionalPhone ?? '', false);
      if (!profPhoneResult.isValid) {
        errors.professionalPhone = getValidationErrorMessage(
          profPhoneResult.error || 'validation.phoneInvalid',
          t
        );
        isValid = false;
      }
      const website = formData.website?.trim() ?? '';
      if (website) {
        const urlResult = validation.validateUrl(website, false);
        if (!urlResult.isValid) {
          errors.website = getValidationErrorMessage(
            urlResult.error || 'Please enter a valid URL',
            t
          );
          isValid = false;
        }
      }
    }

    if (sectionId === 'studies') {
      const studyErrors = validateStudiesList(formData.studies, t, {
        requireAtLeastOne: isStudying,
      });
      Object.assign(errors, studyErrors);
      if (Object.keys(studyErrors).length > 0) {
        isValid = false;
      }
    }

    if (sectionId === 'committees') {
      if (!formData.mainCommittee) {
        setError(t('register.committees.mainRequired'));
        isValid = false;
      } else if (formData.additionalCommittees.length > 3) {
        setError(t('register.committees.maxReached'));
        isValid = false;
      }
    }

    if (!isValid && sectionId !== 'committees') {
      setError(Object.values(errors)[0] || t('register.formErrorsBelow'));
    }
    setFieldErrors(errors);
    return isValid;
  };

  const handleSaveSection = async (sectionId: ProfileSectionId): Promise<void> => {
    if (!id || !member || !formData) {
      throw new Error(t('profile.notFound'));
    }
    try {
      const payload = buildAdminSectionPayload(formData, member, committees, sectionId);
      const updated = await adminService.updateMember(id, payload);
      const activeCommittees = committees.length > 0 ? committees : await loadCommittees();
      setMember(updated);
      setFormData(toAdminForm(updated, activeCommittees, i18n.language));
      showSuccess(t('profile.updateSuccess'));
    } catch (err) {
      const raw = err instanceof Error ? err.message : t('admin.member.updateError');
      const message = getApiErrorMessage(raw, t);
      showError(message);
      setError(message);
      throw new Error(message);
    }
  };

  if (loading) {
    return <LoadingSpinner fullPage />;
  }

  if (!member || !formData) {
    return (
      <FormContainer>
        <CardWithPadding variant="elevated">
          <Stack direction="column" spacing="md">
            <Text color="secondary">{error || t('profile.notFound')}</Text>
            <Button variant="outline" onClick={() => navigate('/admin/members')}>
              {t('admin.member.backToMembers')}
            </Button>
          </Stack>
        </CardWithPadding>
      </FormContainer>
    );
  }

  return (
    <FormContainer>
      <CardWithPadding variant="elevated" headerVariant="gradient" accentBorder="top" pattern="dots" patternIntensity="subtle">
        <Stack direction="column" spacing="lg">
          <CenteredDiv>
            <HeadingWithMargin variant="h1" color="primary" gradient>
              {t('admin.member.edit')} {getMemberDisplayFirstName(member)}{' '}
              {getMemberDisplayLastName(member)}
            </HeadingWithMargin>
            <Text color="secondary" size="lg">
              {t('profile.adminEditSubtitle')}
            </Text>
          </CenteredDiv>

          <BackLink to={`/admin/members/${id}`}>← {t('common.back')}</BackLink>

          <VignetteNote>
            <VignetteStatusBadge
              status={resolveVignetteStatus({
                vignetteStatus: member.vignetteStatus,
              } as IndividualMemberDto)}
            />
            <Text size="sm" color="secondary">
              {t('vignette.management.editHint')}
            </Text>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => navigate('/admin/vignettes')}
            >
              {t('vignette.management.openPanel')}
            </Button>
          </VignetteNote>

          <MemberProfileSectionEditor
            formData={formData}
            setFormData={setFormData as React.Dispatch<React.SetStateAction<any>>}
            committees={committees}
            committeesLoadError={committeesLoadError}
            onRetryCommittees={loadCommittees}
            onOpenCommitteesInfo={() => setCommitteesModalOpen(true)}
            fieldErrors={fieldErrors}
            t={t}
            validateSection={validateSection}
            onSaveSection={handleSaveSection}
            sectionError={error}
            onClearSectionError={() => {
              setError(null);
              setFieldErrors({});
            }}
            personalExtra={
              <>
                <FormField
                  id="admin-member-email"
                  name="email"
                  label={t('common.email')}
                  type="email"
                  autoComplete="email"
                  value={formData.email}
                  error={fieldErrors.email}
                  onChange={e =>
                    setFormData(prev => (prev ? { ...prev, email: e.target.value } : prev))
                  }
                />
                <FormField
                  id="admin-member-registration-year"
                  name="registrationYear"
                  label={t('profile.registrationYear')}
                  type="number"
                  value={String(formData.registrationYear)}
                  onChange={e =>
                    setFormData(prev =>
                      prev
                        ? {
                            ...prev,
                            registrationYear:
                              parseInt(e.target.value, 10) || prev.registrationYear,
                          }
                        : prev
                    )
                  }
                />
              </>
            }
            extraSnapshotKeys={{ personal: PERSONAL_SNAPSHOT_KEYS }}
            includeHonoraryMembership
            showOrganisationName
            showAllSections
            directSave
          />
        </Stack>
      </CardWithPadding>

      <Modal
        isOpen={committeesModalOpen}
        onClose={() => setCommitteesModalOpen(false)}
        title={t('register.aboutCommittees')}
        maxWidth="560px"
      >
        <TextWithMargin size="sm" color="secondary">
          {t('register.committeesInfoIntro')}
        </TextWithMargin>
        <ExternalLink href={t('register.committeesLink')} target="_blank" rel="noopener noreferrer">
          {t('register.committeesLink')}
        </ExternalLink>
        <CommitteesList>
          {COMMITTEES_INFO_LIST.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </CommitteesList>
      </Modal>
    </FormContainer>
  );
};

export default AdminEditMember;
